/**
 * WorkflowStep type definitions shared across canvas components.
 * Icons use Lucide React components for full consistency across palette and canvas.
 */
import {
  Clock,
  Wrench,
  Zap,
  Diamond,
  Link2,
  Upload,
  Download,
  RefreshCw,
  type LucideIcon,
} from 'lucide-react';

export type StepType = 'wait' | 'tool' | 'skill' | 'condition' | 'callback' | 'emit_event' | 'wait_event' | 'loop';
export type StepStatus = 'unconfigured' | 'configured' | 'warning';

export interface MappingRow {
  key: string;
  value: string;
}

export interface CanvasStep {
  id: string;
  type: StepType;
  name: string;
  // wait
  waitDurationMs?: number;
  // tool
  toolName?: string;
  inputMapping?: MappingRow[];
  // skill
  skillName?: string;
  skillId?: string;
  // condition
  conditionExpression?: string;
  onSuccess?: string;
  onFailure?: string;
  // callback
  callbackKey?: string;
  timeoutMs?: number;
  // emit_event
  connectorId?: string;
  eventType?: string;
  eventPayloadMapping?: MappingRow[];
  // wait_event
  waitEventType?: string;
  waitEventConnectorId?: string;
  waitEventTimeoutMs?: number;
  // loop
  targetStepId?: string;
  maxIterations?: number;
}

/** Returns the validation status of a step */
export function getStepStatus(
  step: CanvasStep,
  availableToolNames: string[],
  availableSkillNames: string[],
): StepStatus {
  switch (step.type) {
    case 'wait':
      return (step.waitDurationMs ?? 0) > 0 ? 'configured' : 'unconfigured';
    case 'tool': {
      if (!step.toolName) return 'unconfigured';
      if (availableToolNames.length > 0 && !availableToolNames.includes(step.toolName)) {
        return 'warning';
      }
      return 'configured';
    }
    case 'skill': {
      if (!step.skillName) return 'unconfigured';
      if (availableSkillNames.length > 0 && !availableSkillNames.includes(step.skillName)) {
        return 'warning';
      }
      return 'configured';
    }
    case 'condition':
      return step.conditionExpression && step.onSuccess && step.onFailure
        ? 'configured'
        : 'unconfigured';
    case 'callback':
      return step.callbackKey ? 'configured' : 'unconfigured';
    case 'emit_event':
      return step.connectorId && step.eventType ? 'configured' : 'unconfigured';
    case 'wait_event':
      return step.waitEventType ? 'configured' : 'unconfigured';
    case 'loop':
      return step.targetStepId ? 'configured' : 'unconfigured';
    default:
      return 'unconfigured';
  }
}

export interface StepMeta {
  Icon: LucideIcon;
  label: string;
  color: string;
}

/** Single source of truth for step icons, labels, and colors */
export const STEP_META: Record<StepType, StepMeta> = {
  wait:       { Icon: Clock,      label: 'Wait',        color: '#6366f1' },
  tool:       { Icon: Wrench,     label: 'Tool',        color: '#fb923c' },
  skill:      { Icon: Zap,        label: 'Skill',       color: '#22c55e' },
  condition:  { Icon: Diamond,    label: 'Condition',   color: '#eab308' },
  callback:   { Icon: Link2,      label: 'Callback',    color: '#a855f7' },
  emit_event: { Icon: Upload,     label: 'Emit Event',  color: '#06b6d4' },
  wait_event: { Icon: Download,   label: 'Wait Event',  color: '#f43f5e' },
  loop:       { Icon: RefreshCw,  label: 'Loop',        color: '#8b5cf6' },
};

export function generateStepId(type: StepType): string {
  const randomPart = Math.random().toString(36).substring(2, 8);
  return `${type}_${randomPart}`;
}

export function defaultStep(type: StepType): CanvasStep {
  const id = generateStepId(type);
  const base = { id, name: STEP_META[type].label, type };
  switch (type) {
    case 'wait':       return { ...base, waitDurationMs: 0 };
    case 'tool':       return { ...base, toolName: '', inputMapping: [] };
    case 'skill':      return { ...base, skillName: '', skillId: '', inputMapping: [] };
    case 'condition':  return { ...base, conditionExpression: '', onSuccess: '', onFailure: '' };
    case 'callback':   return { ...base, callbackKey: '', timeoutMs: 86400000 };
    case 'emit_event': return { ...base, connectorId: '', eventType: '', eventPayloadMapping: [] };
    case 'wait_event': return { ...base, waitEventType: '', waitEventConnectorId: '', waitEventTimeoutMs: 86400000 };
    case 'loop':       return { ...base, targetStepId: '', maxIterations: 10 };
  }
}

/** Serialize canvas steps back to the API format */
export function stepsToApi(steps: CanvasStep[]): object[] {
  return steps.map((s, index) => {
    const base = {
      id: s.id,
      type: s.type,
      name: s.name,
      index,
      ...(s.onSuccess !== undefined ? { onSuccess: s.onSuccess } : {}),
      ...(s.onFailure !== undefined ? { onFailure: s.onFailure } : {}),
    };
    switch (s.type) {
      case 'wait':
        return { ...base, waitDurationMs: s.waitDurationMs ?? 0 };
      case 'tool':
        return {
          ...base,
          toolName: s.toolName,
          inputMapping: Object.fromEntries((s.inputMapping ?? []).map(r => [r.key, r.value])),
        };
      case 'skill':
        return {
          ...base,
          skillName: s.skillName,
          skillId: s.skillId,
          inputMapping: Object.fromEntries((s.inputMapping ?? []).map(r => [r.key, r.value])),
        };
      case 'condition':
        return {
          ...base,
          conditionExpression: s.conditionExpression,
          onSuccess: s.onSuccess,
          onFailure: s.onFailure,
        };
      case 'callback':
        return { ...base, callbackKey: s.callbackKey, timeoutMs: s.timeoutMs ?? 86400000 };
      case 'emit_event':
        return {
          ...base,
          connectorId: s.connectorId,
          eventType: s.eventType,
          eventPayloadMapping: Object.fromEntries((s.eventPayloadMapping ?? []).map(r => [r.key, r.value])),
        };
      case 'wait_event':
        return {
          ...base,
          waitEventType: s.waitEventType,
          waitEventConnectorId: s.waitEventConnectorId,
          waitEventTimeoutMs: s.waitEventTimeoutMs ?? 86400000,
        };
      case 'loop':
        return { ...base, targetStepId: s.targetStepId, maxIterations: s.maxIterations ?? 10 };
    }
  });
}

/** Parse API steps into canvas steps */
export function stepsFromApi(raw: unknown[]): CanvasStep[] {
  return (raw ?? []).map((s: unknown) => {
    const step = s as Record<string, unknown>;
    const type = (step.type as StepType) ?? 'tool';
    const base: CanvasStep = {
      id: (step.id as string) ?? generateStepId(type),
      type,
      name: (step.name as string) ?? STEP_META[type]?.label ?? 'Step',
      ...(step.onSuccess !== undefined ? { onSuccess: step.onSuccess as string } : {}),
      ...(step.onFailure !== undefined ? { onFailure: step.onFailure as string } : {}),
    };
    switch (type) {
      case 'wait':
        return { ...base, waitDurationMs: (step.waitDurationMs as number) ?? 0 };
      case 'tool': {
        const mapping = step.inputMapping as Record<string, string> | null;
        return {
          ...base,
          toolName: (step.toolName as string) ?? '',
          inputMapping: mapping ? Object.entries(mapping).map(([key, value]) => ({ key, value })) : [],
        };
      }
      case 'skill': {
        const mapping = step.inputMapping as Record<string, string> | null;
        return {
          ...base,
          skillName: (step.skillName as string) ?? '',
          skillId: (step.skillId as string) ?? '',
          inputMapping: mapping ? Object.entries(mapping).map(([key, value]) => ({ key, value })) : [],
        };
      }
      case 'condition':
        return {
          ...base,
          conditionExpression: (step.conditionExpression as string) ?? '',
          onSuccess: (step.onSuccess as string) ?? '',
          onFailure: (step.onFailure as string) ?? '',
        };
      case 'callback':
        return {
          ...base,
          callbackKey: (step.callbackKey as string) ?? '',
          timeoutMs: (step.timeoutMs as number) ?? 86400000,
        };
      case 'emit_event': {
        const pm = step.eventPayloadMapping as Record<string, string> | null;
        return {
          ...base,
          connectorId: (step.connectorId as string) ?? '',
          eventType: (step.eventType as string) ?? '',
          eventPayloadMapping: pm ? Object.entries(pm).map(([key, value]) => ({ key, value })) : [],
        };
      }
      case 'wait_event':
        return {
          ...base,
          waitEventType: (step.waitEventType as string) ?? '',
          waitEventConnectorId: (step.waitEventConnectorId as string) ?? '',
          waitEventTimeoutMs: (step.waitEventTimeoutMs as number) ?? 86400000,
        };
      case 'loop':
        return {
          ...base,
          targetStepId: (step.targetStepId as string) ?? '',
          maxIterations: (step.maxIterations as number) ?? 10,
        };
      default:
        return base;
    }
  });
}
